import { useEffect, useMemo, useState } from "react";
import { MapPin, Users, Percent, DollarSign } from "lucide-react";

import Panel from "../Panel";
import Skeleton from "../Skeleton";
import StatRow, { Stat } from "../StatRow";
import { EARNINGS_NOTE } from "../InfoTip";
import { capabilitiesFor } from "../capabilities";
import {
  Badge,
  Empty,
  ErrorText,
  PageStack,
  PrimaryText,
  SecondaryText,
  Table,
  TableScroll,
  Td,
  Th,
  Tr,
} from "../ui";
import { getBusinessDashboardData } from "../../../api/apiEndpoints";
import {
  Kpis,
  Member,
  computeKpis,
  formatCurrency,
  monthEarnings,
} from "../../../utils/partnerMetrics";

interface Gym {
  businessId: string;
  name: string;
  address?: { fullAddress?: string };
  products?: any[];
  permissionRole?: string;
}

interface LocationRow {
  gym: Gym;
  kpis: Kpis;
  earnings: number | null;
}

interface LocationsPageProps {
  gyms: Gym[];
  currentBusinessId?: string;
  onSwitch: (businessId: string) => void;
}

const LocationsPage = ({ gyms, currentBusinessId, onSwitch }: LocationsPageProps) => {
  const [rows, setRows] = useState<LocationRow[] | null>(null);
  const [failed, setFailed] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    Promise.allSettled(gyms.map((g) => getBusinessDashboardData(g.businessId))).then((results) => {
      if (cancelled) return;
      const ok: LocationRow[] = [];
      const bad: string[] = [];
      results.forEach((r, i) => {
        const gym = gyms[i];
        if (r.status !== "fulfilled") {
          bad.push(gym.name);
          return;
        }
        const members: Member[] = r.value?.data || [];
        const canSeeRevenue = capabilitiesFor(gym.permissionRole).canViewRevenue;
        ok.push({
          gym,
          kpis: computeKpis(members),
          earnings: canSeeRevenue ? monthEarnings(members, gym.products || []).totalEarnings : null,
        });
      });
      setRows(ok);
      setFailed(bad);
    });
    return () => {
      cancelled = true;
    };
  }, [gyms]);

  const totals = useMemo(() => {
    if (!rows) return null;
    const sum = (f: (r: LocationRow) => number) => rows.reduce((s, r) => s + f(r), 0);
    const visibleEarnings = rows.filter((r) => r.earnings !== null);
    return {
      active: sum((r) => r.kpis.activeMembers),
      trials: sum((r) => r.kpis.trials),
      upgrades: sum((r) => r.kpis.upgrades),
      earnings: visibleEarnings.length
        ? Math.round(visibleEarnings.reduce((s, r) => s + (r.earnings || 0), 0) * 100) / 100
        : null,
    };
  }, [rows]);

  const stats: Stat[] = totals
    ? [
        { label: "Locations", value: String(gyms.length), icon: MapPin },
        { label: "Active Members", value: String(totals.active), icon: Users, hint: "All locations" },
        {
          label: "Trials → Upgrades",
          value: `${totals.trials} / ${totals.upgrades}`,
          icon: Percent,
          hint: "Active right now",
        },
      ]
    : [];
  if (totals?.earnings !== null && totals?.earnings !== undefined) {
    stats.push({
      label: "Earnings This Month",
      value: formatCurrency(totals.earnings),
      icon: DollarSign,
      hint: "All locations",
      info: EARNINGS_NOTE,
    });
  }

  return (
    <PageStack>
      <StatRow stats={stats} loading={!rows} skeletonCount={4} />
      <Panel title="Location Comparison" subtitle="Select a location to switch the dashboard to it">
        {!rows ? (
          Array.from({ length: gyms.length || 2 }).map((_, i) => (
            <Skeleton key={i} $h="40px" $mb="8px" />
          ))
        ) : rows.length === 0 ? (
          <Empty>Couldn't load your locations.</Empty>
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>Location</Th>
                  <Th $align="right">Active</Th>
                  <Th $align="right">Trials</Th>
                  <Th $align="right">Upgrades</Th>
                  <Th $align="right">Lifetime</Th>
                  <Th $align="right">Conversion</Th>
                  <Th $align="right">Earnings this month</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <Tr key={r.gym.businessId} $clickable onClick={() => onSwitch(r.gym.businessId)}>
                    <Td>
                      <PrimaryText>
                        {r.gym.name}{" "}
                        {r.gym.businessId === currentBusinessId && <Badge $tone="green">Viewing</Badge>}
                      </PrimaryText>
                      {r.gym.address?.fullAddress && (
                        <SecondaryText>{r.gym.address.fullAddress}</SecondaryText>
                      )}
                    </Td>
                    <Td data-label="Active" $align="right">{r.kpis.activeMembers}</Td>
                    <Td data-label="Trials" $align="right">{r.kpis.trials}</Td>
                    <Td data-label="Upgrades" $align="right">{r.kpis.upgrades}</Td>
                    <Td data-label="Lifetime" $align="right">{r.kpis.totalMembers}</Td>
                    <Td data-label="Conversion" $align="right">{r.kpis.conversionRate}%</Td>
                    <Td data-label="Earnings this month" $align="right">{r.earnings === null ? "—" : formatCurrency(r.earnings)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
        {failed.length > 0 && <ErrorText>Couldn't load: {failed.join(", ")}</ErrorText>}
      </Panel>
    </PageStack>
  );
};

export default LocationsPage;
